import { FC, memo, Dispatch, SetStateAction, useState } from 'react'
import { CreditNotesView } from './types'
import {
  Text,
  MagicTableCell,
  MagicTableCellLink,
  MagicTableCellCreditNoteStatus,
  MagicTable,
  useMagicTableInfiniteQuery,
  MagicTableFilterConfig,
  useLinkMagicTableWithSearchParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  MagicTableCellEmpty,
  ActiveFilter,
  SequenceMenu,
  SetIsOpen,
  Badge,
  Button,
  SequenceMenuItemType,
  IconCalendar,
  IconHashtag,
  IconPlus,
  IconColumns,
  IconReceiptCredit,
  Flex
} from '@sequencehq/core-components'
import {
  formatISODate,
  hasCreditNoteBeenApplied,
  localDateWithFormat,
  pastRelativeToDate
} from '@sequencehq/utils'
import {
  ViewCreditNoteMenuItem,
  SendCreditNoteMenuItem,
  VoidCreditNoteMenuItem,
  DeleteCreditNoteMenuItem,
  DownloadCreditNoteMenuItem,
  RecalculateCreditNoteMenuItem
} from 'CreditNotes/menuItems'
import { RecalculationFlipResultDialog } from 'CreditNotes/components/dialog/RecalculationFlipResultDialog'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import Page from 'components/Page'
import { StickyPanelFrame } from 'components/Page/StickyPanelFrame'
import { useCreditNotePdfDownload } from './useCreditNotePdfDownload'
import { CreditNotesExportToCsv } from './CreditNotesExportToCsv'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'
import { CreditNote } from 'CreditNotes/types.ts'
import { CREDIT_NOTE_STATUSES } from 'CreditNotes/constants'
import { formatDateRange } from '@sequencehq/utils/dates'
import { CreditNotesSegmentedFilters } from './CreditNotesSegmentedFilters'
import { useNavigate } from 'react-router-dom'
import { customerIdFilterConfig } from 'Customer/index'
import { TableLinksLogos } from 'modules/Integrations'
import { ProtectedButton } from 'lib/rbac/ProtectedButton'
import { usePermissions } from 'lib/rbac'
import { CreateCreditNoteModal } from './modals/CreateCreditNoteModal'
import {
  allCreditNoteStatuses,
  toCreditNoteStatusBadgeProps,
  toMoney
} from '@sequencehq/utils'
import { SavedViewEditor } from 'SavedViews/components/SavedViewEditor'
import { SavedViewTitle } from 'SavedViews/components/SavedViewTitle'
import { useSavedView } from 'SavedViews/hooks/useSavedView'
import { useForcedActiveFilters } from 'SavedViews/hooks/useForcedActiveFilters'

type CreditNoteStatus =
  Dashboardv99990101Api.GetCreditNote.GetCreditNoteResponse['status']

const CreditNotes: FC<{
  title?: string
  forcedActiveFilters?: ActiveFilter[]
  kebabMenuItems?: (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ) => SequenceMenuItemType[] | undefined
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
  showSegmentedFilters?: boolean
}> = memo(
  ({
    title = 'Credit notes',
    forcedActiveFilters = [],
    kebabMenuItems,
    onUpdateSavedFilters,
    showSegmentedFilters = true
  }) => {
    const navigate = useNavigate()
    const { downloadPdf } = useCreditNotePdfDownload()

    const { can } = usePermissions()

    const [createdInvoiceId, setCreatedInvoiceId] = useState<string | null>(
      null
    )
    const [showCreateCreditNoteModal, setShowCreateCreditNoteModal] =
      useState(false)

    const {
      isSavedView,
      savingFilters,
      setSavingFilters,
      forceNewSavedView,
      onSaveFilters,
      onDismissSaveFilters,
      showSavedViewEditor
    } = useSavedView()

    const statusFilterOptions = () => {
      return allCreditNoteStatuses.map(status => ({
        label: toCreditNoteStatusBadgeProps({ status }).children,
        value: status
      }))
    }

    const sentFilterOptions = [
      {
        label: '1 week ago',
        value: pastRelativeToDate(new Date(), { weeks: 1 })
      },
      {
        label: '1 month ago',
        value: pastRelativeToDate(new Date(), { months: 1 })
      },
      {
        label: '3 months ago',
        value: pastRelativeToDate(new Date(), { months: 3 })
      },
      {
        label: '6 months ago',
        value: pastRelativeToDate(new Date(), { months: 6 })
      }
    ]

    const filters: MagicTableFilterConfig<CreditNotesView['controlFilters']>[] =
      [
        {
          ...customerIdFilterConfig,
          paramName: 'customerId'
        },
        {
          type: 'text',
          paramName: 'searchCreditNoteNumber',
          label: 'Credit note number',
          icon: IconHashtag
        },
        {
          type: 'multiSelect',
          paramName: 'creditNoteStatus',
          options: statusFilterOptions(),
          format: status => (
            <Badge
              {...toCreditNoteStatusBadgeProps({
                status: status as CreditNoteStatus
              })}
            />
          ),
          label: 'Status',
          optionsSortFn: () => 0,
          icon: IconColumns
        },
        {
          type: 'date',
          paramName: 'sent',
          paramNameBefore: 'sentBefore',
          paramNameAfter: 'sentAfter',
          optionsBefore: sentFilterOptions,
          optionsAfter: sentFilterOptions,
          format: (_value, label) => label,
          label: 'Sent',
          stringifyDate: date => formatISODate(date),
          icon: IconCalendar
        }
      ]

    const {
      activeFilters: searchActiveFilters,
      onChangeActiveFilters: onChangeSearchActiveFilters,
      sortBy,
      onChangeSortBy
    } = useLinkMagicTableWithSearchParams(filters)

    const { activeFilters, onChangeActiveFilters } = useForcedActiveFilters({
      forcedActiveFilters,
      searchActiveFilters,
      onChangeSearchActiveFilters
    })

    const { infiniteQuery } = useMagicTableInfiniteQuery(
      dashboardv99990101Client.getCreditNotes,
      filters,
      activeFilters,
      sortBy
    )

    const richTitle = showSavedViewEditor ? (
      <SavedViewEditor
        activeFilters={savingFilters || []}
        sortBy={sortBy}
        onDismiss={onDismissSaveFilters}
        forceNewSavedView={forceNewSavedView}
        entityType="CREDIT_NOTES"
      />
    ) : (
      <SavedViewTitle
        title={title}
        kebabMenuItems={kebabMenuItems?.(setSavingFilters)}
      />
    )

    return (
      <>
        {showCreateCreditNoteModal && (
          <CreateCreditNoteModal
            onClose={() => setShowCreateCreditNoteModal(false)}
          />
        )}
        <StickyPanelFrame>
          <Page
            title={title}
            richTitle={richTitle}
            paddingBottom={0}
            menuButtons={savingFilters ? null : <CreditNotesExportToCsv />}
            addNewConfig={
              savingFilters
                ? undefined
                : {
                    button: (
                      <ProtectedButton permission="org.creditNote:create">
                        <Button
                          variant="primary"
                          leadingIcon={<IconPlus color="inherit" />}
                          onClick={() => setShowCreateCreditNoteModal(true)}
                          data-testid="creditNotes.createCreditNote"
                        >
                          New credit note
                        </Button>
                      </ProtectedButton>
                    )
                  }
            }
          >
            <CurrentUserId>
              {userId => (
                <MagicTable<CreditNotesView>
                  activeFilters={activeFilters}
                  onChangeActiveFilters={onChangeActiveFilters}
                  sortBy={sortBy}
                  onChangeSortBy={onChangeSortBy}
                  entityNamePlural="credit notes"
                  infiniteQuery={infiniteQuery}
                  rowPath={(row: CreditNotesView['model']) =>
                    `/credit-notes/${row.id}`
                  }
                  sequenceUserId={userId}
                  columns={[
                    {
                      id: 'grossTotal',
                      header: 'Amount',
                      accessorFn: row => ({
                        currency: row.currency,
                        value: row.grossTotal
                      }),
                      cell: value => (
                        <MagicTableCell
                          text={toMoney(
                            value.getValue<{
                              currency: CreditNote['currency']
                              value: CreditNote['grossTotal']
                            }>()
                          )}
                          textAlign={value.column.columnDef.meta?.textAlign}
                        />
                      ),
                      meta: { textAlign: 'right' }
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      accessorFn: row => ({
                        status: row.status,
                        applicationStatus: row.applicationStatus
                      }),
                      cell: value => {
                        const { status, applicationStatus } = value.getValue<{
                          status: CreditNote['status']
                          applicationStatus: CreditNote['applicationStatus']
                        }>()

                        return (
                          <MagicTableCellCreditNoteStatus
                            status={status}
                            applicationStatus={applicationStatus}
                          />
                        )
                      }
                    },
                    {
                      id: 'creditNoteNumber',
                      header: 'Credit note #',
                      accessorKey: 'creditNoteNumber',
                      cell: value => (
                        <MagicTableCell
                          text={
                            value.getValue<CreditNote['creditNoteNumber']>() ||
                            'Pending'
                          }
                        />
                      )
                    },
                    {
                      id: 'invoice',
                      header: 'Invoice',
                      // The cell owns its own link to the invoice, so the row
                      // link must not wrap it.
                      hasLink: true,
                      accessorFn: row => ({
                        invoiceId: row.invoiceId,
                        invoiceNumber: row.invoiceNumber
                      }),
                      cell: value => {
                        const { invoiceId, invoiceNumber } = value.getValue<{
                          invoiceId: CreditNote['invoiceId']
                          invoiceNumber: CreditNote['invoiceNumber']
                        }>()

                        if (!invoiceId) {
                          return <MagicTableCell text="" />
                        }

                        return (
                          <MagicTableCellLink
                            data-testid={`creditNotes.${value.row.original.id}.invoice`}
                            to={`/invoices/${invoiceId}`}
                            label={invoiceNumber ?? 'View invoice'}
                          />
                        )
                      }
                    },
                    {
                      id: 'customer',
                      header: 'Customer',
                      accessorFn: row => ({
                        customerId: row.customerId
                      }),
                      cell: value => {
                        const { customerId } = value.getValue<{
                          customerId: CreditNote['customerId']
                        }>()

                        return (
                          <Flex
                            alignItems="center"
                            justifyContent="space-between"
                            height="40px"
                            pl="6px"
                          >
                            <CustomerPreviewCardPill
                              customerId={customerId}
                              variant="ghost"
                            />
                          </Flex>
                        )
                      }
                    },
                    {
                      id: 'billingPeriod',
                      header: 'Billing Period',
                      accessorFn: row => ({
                        billingPeriodStart: row.billingPeriodStart,
                        billingPeriodEnd: row.billingPeriodEnd
                      }),
                      cell: value => {
                        const { billingPeriodStart, billingPeriodEnd } =
                          value.getValue<{
                            billingPeriodStart: CreditNote['billingPeriodStart']
                            billingPeriodEnd: CreditNote['billingPeriodEnd']
                          }>()

                        return (
                          <MagicTableCell
                            text={
                              billingPeriodStart && billingPeriodEnd
                                ? formatDateRange({
                                    from: new Date(billingPeriodStart),
                                    to: new Date(billingPeriodEnd)
                                  })
                                : ''
                            }
                          />
                        )
                      }
                    },
                    {
                      id: 'issueDate',
                      header: 'Sent',
                      accessorKey: 'issueDate',
                      cell: value => (
                        <MagicTableCell
                          text={
                            localDateWithFormat(
                              value.getValue<CreditNote['issueDate']>(),
                              'd MMM'
                            ) || ''
                          }
                        />
                      )
                    },
                    {
                      id: 'linkedServices',
                      header: 'Links',
                      accessorKey: 'linkedServices',
                      cell: row => {
                        const linkedServices =
                          row.getValue<CreditNote['linkedServices']>()

                        if (!linkedServices.length) {
                          return null
                        }

                        return (
                          <MagicTableCellEmpty>
                            <TableLinksLogos
                              services={linkedServices.map(
                                l => l.externalService
                              )}
                            />
                          </MagicTableCellEmpty>
                        )
                      }
                    }
                  ]}
                  emptyStateUnfiltered={entityNamePlural => (
                    <MagicTableEmptyState
                      data-testid="creditNotes.emptyState"
                      entityNamePlural={entityNamePlural}
                      imageSrc="/img/empty/invoices.png"
                      learnMoreText="Use credit notes to make corrections to previously issued invoices"
                      learnMoreHref="https://docs.sequencehq.com/invoicing/credit-notes"
                      buttonNew={
                        <ProtectedButton permission="org.creditNote:create">
                          <Button
                            variant="primary"
                            leadingIcon={<IconPlus />}
                            onClick={() => setShowCreateCreditNoteModal(true)}
                          >
                            New credit note
                          </Button>
                        </ProtectedButton>
                      }
                    />
                  )}
                  emptyStateFiltered={(entityNamePlural, clearFilters) => (
                    <MagicTableFilterEmptyState
                      entityNamePlural={entityNamePlural}
                      entityIcon={IconReceiptCredit}
                      actionClearFilters={clearFilters}
                      actionViewAll={() => navigate('/credit-notes')}
                      variant={isSavedView ? 'SAVED_VIEW' : 'TABLE'}
                    />
                  )}
                  kebabMenu={(model: CreditNote, props) => {
                    const isDraft = model.status === CREDIT_NOTE_STATUSES.DRAFT
                    const isSent = model.status === CREDIT_NOTE_STATUSES.SENT
                    const isInProgress =
                      model.status === CREDIT_NOTE_STATUSES.IN_PROGRESS
                    const canUpdate = can('org.creditNote:update')
                    const isApplied = hasCreditNoteBeenApplied(
                      model.applicationStatus
                    )
                    const canSend = can('org.creditNote:send')
                    const canRecalculate =
                      canUpdate &&
                      (isDraft || isInProgress) &&
                      Boolean(model.billingScheduleId)

                    return (
                      <SequenceMenu
                        data-testid={`creditNotes.${model.id}.menu`}
                        buttonSize="small"
                        alignment="bottom-right"
                        items={[
                          ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                            <ViewCreditNoteMenuItem
                              key="view"
                              creditNoteId={model.id}
                              label="View credit note"
                              onClose={() => setIsOpen?.(false)}
                            />
                          ),
                          ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                            <DownloadCreditNoteMenuItem
                              key="download"
                              creditNoteId={model.id}
                              creditNoteNumber={model.creditNoteNumber}
                              creditNoteStatus={model.status}
                              downloadPdf={downloadPdf}
                              onClose={() => setIsOpen?.(false)}
                            />
                          ),
                          ...(canRecalculate
                            ? [
                                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                                  <RecalculateCreditNoteMenuItem
                                    key="recalculate"
                                    creditNoteId={model.id}
                                    onClose={() => setIsOpen?.(false)}
                                    onInvoiceCreated={setCreatedInvoiceId}
                                  />
                                )
                              ]
                            : []),
                          ...(canSend && isDraft
                            ? [
                                () => (
                                  <SendCreditNoteMenuItem
                                    key="send"
                                    creditNoteId={model.id}
                                  />
                                )
                              ]
                            : []),
                          ...(canUpdate && (isDraft || isSent) && !isApplied
                            ? [
                                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                                  <VoidCreditNoteMenuItem
                                    key="void"
                                    creditNoteId={model.id}
                                    onClose={() => setIsOpen?.(false)}
                                  />
                                )
                              ]
                            : []),
                          ...(canUpdate && (isDraft || isInProgress)
                            ? [
                                ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
                                  <DeleteCreditNoteMenuItem
                                    key="delete"
                                    creditNoteId={model.id}
                                    onClose={() => setIsOpen?.(false)}
                                  />
                                )
                              ]
                            : [])
                        ]}
                        {...props}
                      />
                    )
                  }}
                  filters={filters}
                  onSaveFilters={
                    onSaveFilters
                      ? () => onSaveFilters(activeFilters)
                      : undefined
                  }
                  onUpdateSavedFilters={
                    onUpdateSavedFilters && isSavedView
                      ? () => onUpdateSavedFilters(activeFilters, sortBy)
                      : undefined
                  }
                  lockedControls={savingFilters && savingFilters.length > 0}
                  additionalToolbarComponentsV3={
                    showSegmentedFilters
                      ? {
                          start: <CreditNotesSegmentedFilters />
                        }
                      : undefined
                  }
                />
              )}
            </CurrentUserId>
          </Page>
        </StickyPanelFrame>
        {createdInvoiceId && (
          <RecalculationFlipResultDialog
            isOpen
            onClose={() => setCreatedInvoiceId(null)}
            resultType="invoice"
            onConfirm={() => {
              navigate(`/invoices/${createdInvoiceId}`, { replace: true })
            }}
          />
        )}
      </>
    )
  }
)

export default CreditNotes
